import {
  createStatusPacket,
  parseQrPacket,
  QrPacketAssembler,
} from '../optical/qr-stream.js';
import { decodeQrRgba } from '../optical/qr.js';
import { decodeEnvelope } from '../protocol/envelope.js';

const LQP_PREFIX = 'LQP1';

/**
 * Bounded link-layer registry. A completed assembler is retained long enough
 * to answer repeated POLLs without delivering the application frame twice.
 */
export class QrTransferRegistry {
  constructor({
    maxActiveTransfers = 4,
    maxCompletedTransfers = maxActiveTransfers * 4,
    transferIdleMs = 45_000,
    completedRetentionMs = 90_000,
  } = {}) {
    assertPositiveInteger(maxActiveTransfers, 'maxActiveTransfers');
    assertPositiveInteger(maxCompletedTransfers, 'maxCompletedTransfers');
    assertPositiveDuration(transferIdleMs, 'transferIdleMs');
    assertPositiveDuration(completedRetentionMs, 'completedRetentionMs');
    this.maxActiveTransfers = maxActiveTransfers;
    this.maxCompletedTransfers = maxCompletedTransfers;
    this.transferIdleMs = transferIdleMs;
    this.completedRetentionMs = completedRetentionMs;
    this.active = new Map();
    this.completed = new Map();
    this.lastNow = null;
  }

  acceptData(inputPacket, now) {
    this.observeTime(now);
    const packet = normalizeQrPacket(inputPacket);
    if (!packet.ok) return packet;
    if (packet.type !== 'DATA') return reject('QR_PACKET_NOT_DATA', 'le registre attend un paquet DATA');
    const expired = this.pruneExpired(now);
    const key = qrTransferKey(packet);
    const completed = this.completed.get(key);
    if (completed) {
      completed.lastSeenAt = now;
      const duplicate = completed.assembler.accept(packet);
      if (!duplicate.ok) return { ...duplicate, key, expired };
      return {
        ok: true,
        code: 'QR_PACKET_COMPLETE_DUPLICATE',
        key,
        complete: true,
        duplicate: true,
        accepted: false,
        delivered: false,
        frame: null,
        received: duplicate.received,
        missing: duplicate.missing,
        expired,
      };
    }

    let entry = this.active.get(key);
    let evicted = [];
    if (!entry) {
      evicted = this.makeActiveCapacity();
      entry = {
        key,
        assembler: new QrPacketAssembler(),
        createdAt: now,
        lastSeenAt: now,
      };
      this.active.set(key, entry);
    }
    entry.lastSeenAt = now;
    const accepted = entry.assembler.accept(packet);
    if (!accepted.ok) return { ...accepted, key, expired, evicted };
    if (!accepted.complete) {
      return {
        ...accepted,
        code: accepted.duplicate ? 'QR_PACKET_DATA_DUPLICATE' : 'QR_PACKET_DATA_ACCEPTED',
        key,
        delivered: false,
        frame: null,
        expired,
        evicted,
      };
    }

    // QrPacketAssembler only exposes complete=true after the transfer CRC32C.
    const frame = decodeEnvelope(accepted.text);
    this.active.delete(key);
    if (!frame.ok) {
      // A transport CRC only proves that every LQP1 token agrees with the
      // transfer checksum. Never retain or acknowledge a completed transfer
      // until the application envelope itself has passed its CRC32C boundary.
      return reject(frame.code, frame.reason, {
        key,
        complete: true,
        transportCrcValid: true,
        delivered: false,
        received: accepted.received,
        missing: accepted.missing,
        expired,
        evicted,
      });
    }
    const finished = {
      key,
      assembler: entry.assembler,
      createdAt: entry.createdAt,
      completedAt: now,
      lastSeenAt: now,
      frame,
    };
    this.completed.set(key, finished);
    const completedEvicted = this.makeCompletedCapacity();
    return {
      ...accepted,
      code: 'QR_PACKET_COMPLETE',
      key,
      transportCrcValid: true,
      delivered: true,
      frame,
      expired,
      evicted: [...evicted, ...completedEvicted],
    };
  }

  statusForPoll(inputPoll, now, renderOptions = {}) {
    this.observeTime(now);
    const poll = normalizeQrPacket(inputPoll);
    if (!poll.ok) return poll;
    if (poll.type !== 'POLL') return reject('QR_PACKET_NOT_POLL', 'un paquet POLL est requis');
    const expired = this.pruneExpired(now);
    const key = qrTransferKey(poll);
    const entry = this.active.get(key) || this.completed.get(key);
    if (!entry) {
      const status = createStatusPacket(poll.sessionId, poll.total, [], {
        ...renderOptions,
        transferCrc: poll.transferCrc,
        roundId: poll.roundId,
        transferValidated: false,
        palette: renderOptions.palette ?? 'mono',
      });
      return {
        ok: true,
        code: 'QR_PACKET_STATUS_EMPTY',
        key,
        known: false,
        complete: false,
        received: [],
        missing: status.missing,
        status,
        expired,
      };
    }

    entry.lastSeenAt = now;
    let status;
    try {
      status = entry.assembler.createStatus(poll, {
        ...renderOptions,
        palette: renderOptions.palette ?? 'mono',
      });
    } catch (error) {
      return reject('QR_PACKET_STATUS_FAILED', error?.message || String(error), { key, expired });
    }
    return {
      ok: true,
      code: status.missing.length ? 'QR_PACKET_STATUS_PARTIAL' : 'QR_PACKET_STATUS_COMPLETE',
      key,
      known: true,
      complete: status.missing.length === 0,
      received: status.received,
      missing: status.missing,
      status,
      expired,
    };
  }

  prune(now) {
    this.observeTime(now);
    return this.pruneExpired(now);
  }

  pruneExpired(now) {
    const expired = [];
    for (const [key, entry] of this.active) {
      if (now - entry.lastSeenAt >= this.transferIdleMs) {
        this.active.delete(key);
        expired.push(key);
      }
    }
    for (const [key, entry] of this.completed) {
      if (now - entry.lastSeenAt >= this.completedRetentionMs) {
        this.completed.delete(key);
        expired.push(key);
      }
    }
    return expired;
  }

  snapshot() {
    return {
      active: [...this.active.keys()],
      completed: [...this.completed.keys()],
      activeCount: this.active.size,
      completedCount: this.completed.size,
    };
  }

  makeActiveCapacity() {
    const evicted = [];
    while (this.active.size >= this.maxActiveTransfers) {
      const key = oldestKey(this.active);
      if (key === null) break;
      this.active.delete(key);
      evicted.push(key);
    }
    return evicted;
  }

  makeCompletedCapacity() {
    const evicted = [];
    while (this.completed.size > this.maxCompletedTransfers) {
      const key = oldestKey(this.completed);
      if (key === null) break;
      this.completed.delete(key);
      evicted.push(key);
    }
    return evicted;
  }

  observeTime(now) {
    assertTimestamp(now);
    if (this.lastNow !== null && now < this.lastNow) throw new RangeError('timestamp registre QR non monotone');
    this.lastNow = now;
  }
}

export class SpatialReceiver {
  constructor(video, {
    scanWidth = 720,
    scanIntervalMs = 100,
    diagnosticIntervalMs = 2_000,
    dedupeReleaseMs = 1500,
    maxActiveTransfers = 4,
    maxCompletedTransfers = maxActiveTransfers * 4,
    transferIdleMs = 45_000,
    completedRetentionMs = 90_000,
    canvas = null,
  } = {}) {
    this.video = video;
    this.scanWidth = scanWidth;
    this.baseScanIntervalMs = scanIntervalMs;
    this.scanIntervalMs = scanIntervalMs;
    assertPositiveDuration(diagnosticIntervalMs, 'diagnosticIntervalMs');
    this.diagnosticIntervalMs = diagnosticIntervalMs;
    this.lastDiagnosticAt = -Infinity;
    this.profile = 'screen-mono';
    this.canvas = canvas || document.createElement('canvas');
    this.context = this.canvas.getContext('2d', { alpha: false, willReadFrequently: true });
    this.lastScanAt = -Infinity;
    this.busy = false;
    this.deduplicator = new FrameDeduplicator(dedupeReleaseMs);
    this.transfers = new QrTransferRegistry({
      maxActiveTransfers,
      maxCompletedTransfers,
      transferIdleMs,
      completedRetentionMs,
    });
    this.onFrame = null;
    this.onMessage = null;
    this.onControl = null;
    this.onStatusRequired = null;
    this.stats = {
      scans: 0,
      qrFound: 0,
      lqpPacketsValid: 0,
      packetCrcRejected: 0,
      transfersCompleted: 0,
      controlsSeen: 0,
      lastScan: null,
      lastQrGeometry: null,
    };
  }

  setProfile(profile) {
    this.profile = profile;
    // Pastel DATA packets remain ordinary binary QR symbols. They must use the
    // same grayscale decoder as monochrome instead of the legacy RGBx3 path.
    this.scanIntervalMs = this.baseScanIntervalMs;
  }

  async ingest(now, metadata = {}) {
    if (this.busy || now - this.lastScanAt < this.scanIntervalMs || this.video.readyState < 2) return;
    this.busy = true;
    this.lastScanAt = now;
    this.stats.scans += 1;
    const started = performance.now();
    let scanDiagnostics = {};
    try {
      const videoWidth = Number(this.video.videoWidth) || 0;
      const videoHeight = Number(this.video.videoHeight) || 0;
      const sourceWidth = videoWidth || 1280;
      const sourceHeight = videoHeight || 720;
      const width = Math.min(this.scanWidth, sourceWidth);
      const height = Math.max(1, Math.round(width * sourceHeight / sourceWidth));
      if (this.canvas.width !== width || this.canvas.height !== height) {
        this.canvas.width = width;
        this.canvas.height = height;
      }
      this.context.drawImage(this.video, 0, 0, width, height);
      const image = this.context.getImageData(0, 0, width, height);
      const optical = decodeMono(image.data, width, height);
      const decodeMs = performance.now() - started;
      const lastScan = {
        atMs: now,
        mediaTime: metadata.mediaTime ?? null,
        presentedFrames: metadata.presentedFrames ?? null,
        videoWidth,
        videoHeight,
        sourceWidth,
        sourceHeight,
        scanWidth: width,
        scanHeight: height,
        scaleX: width / sourceWidth,
        scaleY: height / sourceHeight,
      };
      this.stats.lastScan = lastScan;
      const qrGeometry = describeQrGeometry(optical.location, width, height);
      if (qrGeometry) this.stats.lastQrGeometry = { ...qrGeometry, seenAtMs: now };
      scanDiagnostics = {
        width,
        height,
        scanWidth: width,
        scanHeight: height,
        sourceWidth,
        sourceHeight,
        videoWidth,
        videoHeight,
        scanScaleX: lastScan.scaleX,
        scanScaleY: lastScan.scaleY,
        mediaTime: lastScan.mediaTime,
        presentedFrames: lastScan.presentedFrames,
        location: optical.location || null,
        qrGeometry,
      };
      const diagnostics = {
        decodeMs,
        ...scanDiagnostics,
        rxStats: { ...this.stats },
      };
      if (!optical.ok) {
        this.deduplicator.observeAbsence(now);
        const heartbeat = now - this.lastDiagnosticAt >= this.diagnosticIntervalMs;
        if (heartbeat) this.lastDiagnosticAt = now;
        const result = {
          ok: false,
          code: heartbeat ? 'QR_SCAN_HEARTBEAT' : optical.code,
          observedCode: optical.code,
          diagnosticOnly: heartbeat,
          ...diagnostics,
        };
        this.onFrame?.(result);
        return result;
      }
      this.lastDiagnosticAt = now;
      this.stats.qrFound += 1;
      diagnostics.rxStats = { ...this.stats };
      return this.acceptDecodedText(optical.text, now, diagnostics);
    } catch (error) {
      const result = {
        ok: false,
        code: 'DECODER_EXCEPTION',
        reason: error?.message || String(error),
        decodeMs: performance.now() - started,
        ...scanDiagnostics,
        mediaTime: scanDiagnostics.mediaTime ?? metadata.mediaTime ?? null,
        rxStats: { ...this.stats },
      };
      this.onFrame?.(result);
      return result;
    } finally {
      this.busy = false;
    }
  }

  /** Route one already decoded QR token; exposed for deterministic RX tests. */
  acceptDecodedText(text, now, diagnostics = {}) {
    if (typeof text !== 'string') {
      const result = { ok: false, code: 'QR_PACKET_INVALID', reason: 'contenu QR non textuel', ...diagnostics };
      this.onFrame?.(result);
      return result;
    }
    if (text.startsWith(LQP_PREFIX)) return this.acceptLqpToken(text, now, diagnostics);

    const frame = decodeEnvelope(text);
    if (!frame.ok) {
      this.deduplicator.observeAbsence(now);
      const result = { ok: false, code: frame.code, reason: frame.reason, ...diagnostics };
      this.onFrame?.(result);
      return result;
    }
    const result = { ok: true, code: 'CRC_OK', frame, ...diagnostics };
    this.onFrame?.(result);
    if (this.deduplicator.observeFrame(frame.fingerprint, now)) this.onMessage?.(frame);
    return result;
  }

  acceptLqpToken(text, now, diagnostics) {
    const packet = parseQrPacket(text);
    if (!packet.ok) {
      if (packet.code?.includes('CRC')) this.stats.packetCrcRejected += 1;
      const result = { ...packet, ...diagnostics, rxStats: { ...this.stats } };
      this.onFrame?.(result);
      return result;
    }
    this.stats.lqpPacketsValid += 1;

    if (packet.type === 'DATA') {
      const accepted = this.transfers.acceptData(packet, now);
      if (accepted.delivered) this.stats.transfersCompleted += 1;
      const result = {
        ...accepted,
        packetType: packet.type,
        sessionId: packet.sessionId,
        transferCrc: packet.transferCrc,
        index: packet.index,
        total: packet.total,
        ...diagnostics,
        rxStats: { ...this.stats },
      };
      this.onFrame?.(result);
      if (accepted.delivered && accepted.frame) this.onMessage?.(accepted.frame);
      return result;
    }

    this.stats.controlsSeen += 1;
    const controlDiagnostics = { ...diagnostics, rxStats: { ...this.stats } };
    this.onControl?.(packet, controlDiagnostics);
    if (packet.type === 'POLL') {
      const response = this.transfers.statusForPoll(packet, now);
      const result = {
        ...response,
        packetType: packet.type,
        sessionId: packet.sessionId,
        transferCrc: packet.transferCrc,
        total: packet.total,
        roundId: packet.roundId,
        ...controlDiagnostics,
      };
      this.onFrame?.(result);
      if (response.ok) {
        this.onStatusRequired?.(response.status, {
          poll: packet,
          key: response.key,
          known: response.known,
          complete: response.complete,
          received: response.received,
          missing: response.missing,
        });
      }
      return result;
    }

    // ACK and STATUS are sender-side controls. They are observable, but never
    // cross the application delivery boundary.
    const result = {
      ok: true,
      code: `QR_PACKET_${packet.type}`,
      packetType: packet.type,
      sessionId: packet.sessionId,
      transferCrc: packet.transferCrc ?? null,
      index: packet.index ?? null,
      total: packet.total ?? null,
      roundId: packet.roundId ?? null,
      packet,
      ...controlDiagnostics,
    };
    this.onFrame?.(result);
    return result;
  }
}

export class FrameDeduplicator {
  constructor(releaseAfterMs = 1500) {
    if (!Number.isFinite(releaseAfterMs) || releaseAfterMs < 0) throw new RangeError('dedupeReleaseMs invalide');
    this.releaseAfterMs = releaseAfterMs;
    this.activeFingerprint = null;
    this.lastSeenAt = -Infinity;
  }

  observeFrame(fingerprint, now) {
    const deliver = fingerprint !== this.activeFingerprint;
    this.activeFingerprint = fingerprint;
    this.lastSeenAt = now;
    return deliver;
  }

  observeAbsence(now) {
    if (this.activeFingerprint !== null && now - this.lastSeenAt >= this.releaseAfterMs) {
      this.activeFingerprint = null;
      this.lastSeenAt = -Infinity;
      return true;
    }
    return false;
  }
}

export function qrTransferKey(packet) {
  if (!packet || typeof packet.sessionId !== 'string' || typeof packet.transferCrc !== 'string'
      || !Number.isInteger(packet.total)) {
    throw new TypeError('identité de transfert QR invalide');
  }
  return `${packet.sessionId}:${packet.transferCrc}:${packet.total}`;
}

function normalizeQrPacket(input) {
  if (typeof input === 'string') return parseQrPacket(input);
  if (input?.encodedText) return parseQrPacket(input.encodedText);
  return reject('QR_PACKET_INVALID', 'paquet LQP1 absent ou incomplet');
}

function oldestKey(entries) {
  let selected = null;
  let oldest = Infinity;
  for (const [key, entry] of entries) {
    if (entry.lastSeenAt < oldest) {
      selected = key;
      oldest = entry.lastSeenAt;
    }
  }
  return selected;
}

function decodeMono(data, width, height) {
  const result = decodeQrRgba(data, width, height);
  return result ? { ok: true, text: result.text, location: result.location } : { ok: false, code: 'NO_2D_CODE' };
}

function describeQrGeometry(location, scanWidth, scanHeight) {
  if (!location || !Number.isFinite(scanWidth) || !Number.isFinite(scanHeight)
      || scanWidth <= 0 || scanHeight <= 0) return null;
  const points = [
    location.topLeftCorner,
    location.topRightCorner,
    location.bottomRightCorner,
    location.bottomLeftCorner,
  ];
  if (points.some((point) => !Number.isFinite(point?.x) || !Number.isFinite(point?.y))) return null;
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const left = Math.min(...xs);
  const right = Math.max(...xs);
  const top = Math.min(...ys);
  const bottom = Math.max(...ys);
  const sides = points.map((point, index) => {
    const next = points[(index + 1) % points.length];
    return Math.hypot(point.x - next.x, point.y - next.y);
  });
  const areaPx2 = Math.abs(points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length];
    return sum + point.x * next.y - next.x * point.y;
  }, 0)) / 2;
  return {
    center: {
      x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
      y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
    },
    bounds: {
      left,
      top,
      right,
      bottom,
      width: right - left,
      height: bottom - top,
    },
    minSidePx: Math.min(...sides),
    maxSidePx: Math.max(...sides),
    areaPx2,
    widthFraction: (right - left) / scanWidth,
    heightFraction: (bottom - top) / scanHeight,
    areaFraction: areaPx2 / (scanWidth * scanHeight),
  };
}

function assertTimestamp(now) {
  if (!Number.isFinite(now) || now < 0) throw new RangeError('timestamp registre QR invalide');
}
function assertPositiveInteger(value, label) {
  if (!Number.isInteger(value) || value < 1) throw new RangeError(`${label} doit être un entier strictement positif`);
}
function assertPositiveDuration(value, label) {
  if (!Number.isFinite(value) || value <= 0) throw new RangeError(`${label} doit être strictement positif`);
}
function reject(code, reason, details = {}) { return { ok: false, code, reason, ...details }; }
